import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { ArrowRight, ChevronRight, Repeat, X } from 'lucide-react';
import {
  buildComingUp,
  comingUpAction,
  comingUpChip,
  comingUpSlots,
  comingUpState,
  showsShowAll,
  type ComingUpAction,
  type ComingUpChip,
  type ComingUpGoalInput,
  type ComingUpItem,
  type ComingUpRead,
  type ComingUpRoutineInput,
  type ComingUpSlot,
  type ComingUpTaskInput,
} from '../../../lib/coming-up';
import { GOAL_INTENTS, goalIntentContext } from '../../../lib/goal-intents';
import { fromCardBackground } from '../../../lib/card-background-click';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import { useAgentName } from '../../../lib/use-agent-name';
import { AgentDot } from '../../AgentDot';
import { Modal } from '../../Modal';
import { MaestroMark } from '../MaestroMark';
import { Badge, Button, Card, IconButton, ScrollArea, TextButton, Typography, type BadgeVariant } from '../ui';
import { HOME3_PAGE } from './Home3Feed';
import styles from './Home3ComingUp.module.css';

/**
 * The hidden line that rides beside "What should I do next for my business?", so the Maestro knows the
 * ask came from an empty Home and does not have to guess it from the words. Byte-identical on iOS and
 * Android.
 */
export const ASK_MAESTRO_CONTEXT =
  'The user tapped Ask Maestro on an empty Home Coming up section: nothing is scheduled for the next 7 days. They want concrete next steps for their business.';

const SUGGESTED_GOAL_IDS = GOAL_INTENTS.map((goal) => goal.id);

const ACTION_KEY: Record<ComingUpAction, string> = {
  showCalendar: 'home.comingUp.showCalendar',
  showTask: 'home.comingUp.showTask',
  showRoutine: 'home.comingUp.showRoutine',
  showGoal: 'home.comingUp.showGoal',
};

const CHIP_VARIANT: Record<ComingUpChip['kind'], BadgeVariant> = {
  overdue: 'failed',
  allDay: 'in-progress',
  tomorrowAllDay: 'in-progress',
  today: 'in-progress',
  tomorrow: 'in-progress',
  inDays: 'in-progress',
  routine: 'in-progress',
};

export interface Home3ComingUpProps {
  tasks: readonly ComingUpTaskInput[];
  routines: readonly ComingUpRoutineInput[];
  goals: readonly ComingUpGoalInput[];
  /** Without a connected calendar the first slot is not the calendar's: all three are Symphony's own. */
  calendarConnected: boolean;
  calendarItems: readonly NonNullable<Parameters<typeof buildComingUp>[0]['calendarItems']>[number][];
  reads: { tasks: ComingUpRead; routines: ComingUpRead; goals: ComingUpRead; meetings: ComingUpRead; bookings: ComingUpRead };
  liveWorkspaceIds: ReadonlySet<string> | null;
  agentNames: Record<string, string>;
  spaceNames: Record<string, string>;
  timeZone: string;
  /** The phone layout: cards in a horizontal row. Otherwise the desktop list beside the brief. */
  narrow: boolean;
  onOpen: (item: ComingUpItem) => void;
  onRetry: () => void;
}

/**
 * The home's "Coming up" section (Figma 21417:120782 desktop, 21417:134808 phone) — three slots of what
 * happens next and what needs the reader. Every rule
 * about WHICH items, their order and their chip is `lib/coming-up.ts`; this component only draws.
 *
 * The desktop draws the slots as rows beside the brief; the phone as cards in a horizontal row whose
 * next card peeks. "Show all" opens every real item in a modal.
 */
export function Home3ComingUp({
  tasks,
  routines,
  goals,
  calendarConnected,
  calendarItems,
  reads,
  liveWorkspaceIds,
  agentNames,
  spaceNames,
  timeZone,
  narrow,
  onOpen,
  onRetry,
}: Home3ComingUpProps) {
  const { t, i18n } = useTranslation();
  const { logPageClickBi, logModalActionBi } = useGenericBi();
  const openSideChat = useOpenSideChat();
  const maestroName = useAgentName().short;
  const [showAll, setShowAll] = useState(false);
  const closeShowAll = () => {
    logModalActionBi({ page_name: HOME3_PAGE, modal_name: 'coming_up_show_all', action: 'close' });
    setShowAll(false);
  };
  const now = Date.now();
  const { cards, all } = useMemo(
    () => buildComingUp({ tasks, routines, goals, calendarConnected, calendarItems, liveWorkspaceIds, now }),
    // `now` moves every render; the section refreshes with its reads, never on a clock (spec: no live ticking).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tasks, routines, goals, calendarConnected, calendarItems, liveWorkspaceIds],
  );
  const slots = useMemo(() => comingUpSlots(cards, SUGGESTED_GOAL_IDS), [cards]);
  const { state, partial } = comingUpState(
    [reads.tasks, reads.routines, reads.goals, reads.meetings, reads.bookings],
    all.length,
    SUGGESTED_GOAL_IDS.length,
  );

  const locale = i18n.language || undefined;
  const clock = useCallback(
    (at: number) => new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(at),
    [locale],
  );
  const routineWhen = (at: number) => `${new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(at)} ${clock(at)}`;
  const chipLabel = (chip: ComingUpChip): string => {
    switch (chip.kind) {
      case 'overdue':
        return t('home.comingUp.chip.overdue');
      case 'allDay':
        return t('calendar.grid.allDay');
      case 'tomorrowAllDay':
        return t('calendar.agenda.tomorrow');
      case 'today':
        return t('home.comingUp.chip.today', { time: clock(chip.at) });
      case 'tomorrow':
        return t('home.comingUp.chip.tomorrow', { time: clock(chip.at) });
      case 'inDays':
        return t('home.comingUp.chip.inDays', { count: chip.days });
      case 'routine':
        return t('home.comingUp.chip.routine', { when: routineWhen(chip.at) });
    }
  };

  const open = (item: ComingUpItem) => {
    logPageClickBi(
      { page_name: HOME3_PAGE, button_name: `coming_up_open_${item.kind}`, ...(item.target.type === 'task' ? { task_id: item.target.id } : {}) },
      { componentName: 'coming-up' },
    );
    onOpen(item);
  };

  const askMaestro = () => {
    logPageClickBi({ page_name: HOME3_PAGE, button_name: 'coming_up_ask_maestro' });
    openSideChat({ kind: 'new', message: t('home.comingUp.allClear.seed'), context: ASK_MAESTRO_CONTEXT });
  };

  const retry = () => {
    logPageClickBi({ page_name: HOME3_PAGE, button_name: 'coming_up_retry' });
    onRetry();
  };

  const who = (item: ComingUpItem): { mark: ReactNode; text: string } => {
    const space = item.workspaceId ? spaceNames[item.workspaceId] ?? '' : '';
    const agent = item.agentId ? agentNames[item.agentId] ?? '' : item.maestro ? maestroName : '';
    const mark = item.agentId ? <AgentDot id={item.agentId} size={16} /> : item.maestro ? <MaestroMark size={16} /> : null;
    const place = space || item.sourceLabel || '';
    return { mark: agent ? mark : null, text: [agent, place].filter(Boolean).join(' | ') };
  };

  const startGoal = (goalId: string) => {
    const goal = GOAL_INTENTS.find((g) => g.id === goalId);
    if (!goal) return;
    logPageClickBi({ page_name: HOME3_PAGE, button_name: 'coming_up_start_goal' }, { componentName: `build-band-${goal.id}` });
    openSideChat({ kind: 'new', message: t(goal.seedKey), context: goalIntentContext(goal) });
  };

  const goalView = (goalId: string) => {
    const goal = GOAL_INTENTS.find((g) => g.id === goalId);
    const chip = <Badge variant="waiting-on-a-reply" size="tiny" label={t('home.comingUp.chip.goal')} />;
    const title = goal ? t(goal.titleKey) : '';
    return {
      key: `goal:${goalId}`,
      chip,
      rowChip: chip,
      title,
      rowTitle: title,
      sub: { mark: null as ReactNode, text: '' },
      action: t('home.comingUp.startGoal'),
      run: () => startGoal(goalId),
    };
  };

  const viewOf = (slot: ComingUpSlot) => (slot.type === 'goal' ? goalView(slot.goalId) : slotView(slot.item));

  const slotView = (item: ComingUpItem) => {
    const chip = comingUpChip(item, now, timeZone);
    const cardChip = (
      <Badge
        variant={CHIP_VARIANT[chip.kind]}
        size="tiny"
        label={chipLabel(chip)}
        startIcon={chip.kind === 'routine' ? <Repeat size={12} aria-hidden /> : undefined}
      />
    );
    // A row says "Routine" in its title (Figma 21639:675067), so its chip keeps only the next run.
    return {
      key: item.id,
      chip: cardChip,
      rowChip:
        chip.kind === 'routine' ? <Badge variant={CHIP_VARIANT.routine} size="tiny" label={routineWhen(chip.at)} /> : cardChip,
      title: item.kind === 'meeting' ? t('home.comingUp.eventTitle', { title: item.title }) : item.title,
      rowTitle:
        chip.kind === 'routine'
          ? t('home.comingUp.routineTitle', { title: item.title })
          : item.kind === 'meeting'
            ? t('home.comingUp.eventTitle', { title: item.title })
            : item.title,
      sub: who(item),
      action: t(ACTION_KEY[comingUpAction(item)]),
      run: () => open(item),
    };
  };

  const subLine = (sub: { mark: ReactNode; text: string }) =>
    sub.text ? (
      <span className={styles.sub}>
        {sub.mark}
        {/* "agent · space" is Small/Regular 14/20 at EVERY width (Figma 22517-592427) — the same 14 as the
            row title but Regular, one leading step taller than p2's 14/18. heading-04 supplies the 14/20;
            `.subText` pins the weight to Regular (the mobile two-weight restore would otherwise bump
            heading-* to Medium). The phone carousel card and show-all sheet draw the same 14/20 Regular the
            native cards' subline now does (iOS `.smallRegular`, Android `Type.smallRegular`). */}
        <Typography variant="heading-04" color="secondary" component="span" className={styles.subText}>
          {sub.text}
        </Typography>
      </span>
    ) : null;

  /** The desktop and modal row: title and who on the left, then the chip, then the button. */
  const row = (slot: ComingUpSlot, onRun?: () => void) => {
    const v = viewOf(slot);
    const run = () => {
      onRun?.();
      v.run();
    };
    return (
      <li key={v.key} className={styles.row} onClick={fromCardBackground(run)}>
        <span className={styles.rowBody}>
          <span className={styles.rowTitleLine}>
            {/* The DESKTOP row title is Figma "Heading 04" (Wix Madefor Display SemiBold 14/20, node
                22252-117950) via the `sym-home-title` marker; it rests on the `h4` role (Small/SemiBold
                14/18), which mobile keeps and the phone card/sheet keep as Small/Medium. This is why the
                row hover no longer bumps the weight (the title already rests at SemiBold); the hover just
                slides the arrow in. */}
            <Typography variant="h4" color="primary" component="span" className={`sym-home-title ${styles.title}`}>
              {v.rowTitle}
            </Typography>
            <ArrowRight size={16} aria-hidden className={`dir-flip ${styles.rowArrow}`} />
          </span>
          {subLine(v.sub)}
        </span>
        {v.rowChip}
        <Button variant="outlined" size="tiny" onClick={run}>
          {v.action}
        </Button>
      </li>
    );
  };

  /** The phone sheet's row (Figma 21997:146144): the card's stack — chip, title, who — with the button top right. */
  const sheetRow = (slot: ComingUpSlot) => {
    const v = viewOf(slot);
    const run = () => {
      setShowAll(false);
      v.run();
    };
    return (
      <li key={v.key} className={styles.row} onClick={fromCardBackground(run)}>
        <span className={`${styles.cardBody} ${styles.sheetRowBody}`}>
          {v.chip}
          <Typography variant="small-medium" color="primary" component="span" className={styles.title}>
            {v.title}
          </Typography>
          {subLine(v.sub)}
        </span>
        <Button variant="outlined" size="tiny" onClick={run}>
          {v.action}
        </Button>
      </li>
    );
  };

  /** The phone card: chip, title and who stacked on the left, the button on the right. */
  const card = (slot: ComingUpSlot) => {
    const v = viewOf(slot);
    return (
      <Card key={v.key} variant="sunken" size="sm" className={styles.card} onClick={fromCardBackground(v.run)}>
        <span className={styles.cardBody}>
          {v.chip}
          <Typography variant="small-medium" color="primary" component="span" className={styles.title}>
            {v.title}
          </Typography>
          {subLine(v.sub)}
        </span>
        <Button variant="outlined" size="tiny" onClick={v.run}>
          {v.action}
        </Button>
      </Card>
    );
  };

  const body = (() => {
    if (state === 'loading') {
      return narrow ? (
        <div className={styles.cards} aria-busy>
          {[0, 1, 2].map((i) => (
            <Card key={i} variant="sunken" size="sm" className={`${styles.card} ${styles.placeholderCard}`}>
              <span className={styles.placeholderLine} />
              <span className={`${styles.placeholderLine} ${styles.placeholderWide}`} />
            </Card>
          ))}
        </div>
      ) : (
        <ul className={styles.rows} aria-busy>
          {[0, 1, 2].map((i) => (
            <li key={i} className={styles.row}>
              <span className={styles.rowBody}>
                <span className={`${styles.placeholderLine} ${styles.placeholderWide}`} />
                <span className={styles.placeholderLine} />
              </span>
            </li>
          ))}
        </ul>
      );
    }
    if (state === 'failed') {
      return (
        <div className={styles.message} role="alert">
          <Typography variant="p2" color="secondary" component="p">
            {t('home.comingUp.failed')}
          </Typography>
          <Button variant="outlined" size="tiny" onClick={retry}>
            {t('home.comingUp.retry')}
          </Button>
        </div>
      );
    }
    if (state === 'allClear') {
      return (
        <div className={styles.message}>
          <Typography variant="h4" color="primary" component="p">
            {t('home.comingUp.allClear.title')}
          </Typography>
          <Typography variant="p2" color="secondary" component="p">
            {t('home.comingUp.allClear.body', { maestroAgentName: maestroName })}
          </Typography>
          <Button variant="outlined" size="tiny" onClick={askMaestro}>
            {t('home.comingUp.allClear.cta', { maestroAgentName: maestroName })}
          </Button>
        </div>
      );
    }
    const list = narrow ? (
      <ScrollArea axis="horizontal" bar="hidden" className={styles.cardsScroller}>
        <div className={styles.cards}>{slots.map(card)}</div>
      </ScrollArea>
    ) : (
      <ul className={styles.rows}>{slots.map((slot) => row(slot))}</ul>
    );
    return (
      <>
        {state === 'pickGoal' && (
          <div className={styles.message}>
            <Typography variant="h4" color="primary" component="p">
              {t('home.comingUp.pickGoal.title')}
            </Typography>
            <Typography variant="p2" color="secondary" component="p">
              {t('home.comingUp.pickGoal.body')}
            </Typography>
          </div>
        )}
        {list}
      </>
    );
  })();

  const sectionContent = (
    <>
      <div className={styles.head}>
        <Typography id="home3-coming-up-title" variant="display-05" color="primary" component="h2">
          {t('home.comingUp.title')}
        </Typography>
        {showsShowAll(state) && (
          // Chevron-right: "Show all" opens the Coming-up modal IN PLACE, so it takes the in-context
          // disclosure glyph rather than the ↗ that means "navigates away" (Figma 21882-133470). This now
          // matches the native Coming-up headers (`HomeComingUpSection.swift` / Android `ComingUpSection.kt`),
          // closing the earlier web-first ↗ divergence.
          <TextButton
            size="small"
            endIcon={<ChevronRight size={16} aria-hidden className="dir-flip" />}
            onClick={() => {
              logPageClickBi({ page_name: HOME3_PAGE, button_name: 'coming_up_show_all' });
              setShowAll(true);
            }}
          >
            {t('home.comingUp.showAll')}
          </TextButton>
        )}
      </div>
      {body}
      {partial && (
        <div className={styles.partial}>
          <Typography variant="p3" color="secondary" component="span">
            {t('home.comingUp.partial')}
          </Typography>
          <TextButton size="tiny" underline onClick={retry}>
            {t('home.comingUp.retry')}
          </TextButton>
        </div>
      )}
      {/* Desktop (Figma 21997:161492): the DS dialog with its header and footer rules and a primary Close.
          Phone (Figma 21997:146144): a bottom sheet with its own Display title and ✕ and no footer; the
          title stays put while the list scrolls under it. */}
      <Modal
        open={showAll}
        onClose={closeShowAll}
        title={narrow ? undefined : t('home.comingUp.title')}
        labelledBy={narrow ? 'home3-coming-up-sheet-title' : undefined}
        size="medium"
        maxHeightPx={650}
        scrollBody={!narrow}
        presentation={narrow ? 'sheet' : 'centered'}
        hideClose={narrow}
        headerSeparator
        footerSeparator
        contentClassName={narrow ? styles.sheetSlot : undefined}
        footer={
          narrow ? undefined : (
            <Button onClick={closeShowAll}>{t('home.comingUp.modal.close')}</Button>
          )
        }
      >
        {narrow ? (
          <>
            <div className={styles.sheetHead}>
              <span id="home3-coming-up-sheet-title" className={styles.sheetTitle}>
                {t('home.comingUp.title')}
              </span>
              <IconButton size="medium" aria-label={t('common.close')} onClick={closeShowAll}>
                <X size={24} aria-hidden />
              </IconButton>
            </div>
            <ScrollArea className={styles.sheetList}>
              <ul className={styles.rows}>{all.map((item) => sheetRow({ type: 'item', item }))}</ul>
            </ScrollArea>
          </>
        ) : (
          <ul className={`${styles.rows} ${styles.modalRows}`}>
            {all.map((item) => row({ type: 'item', item }, () => setShowAll(false)))}
          </ul>
        )}
      </Modal>
    </>
  );

  // Desktop (Figma 22252-117950): the section is a DS SUNKEN card on the stone page, beside the brief.
  // `disablePadding` keeps the section's own 20/26/26 padding (Home3ComingUp.module.css) while the DS
  // supplies the white fill + 20 radius — so this is not a hand-rolled surface (card-spec). The phone
  // keeps the bare horizontal card carousel.
  return narrow ? (
    <section className={`${styles.root} ${styles.narrow}`} aria-labelledby="home3-coming-up-title" data-testid="home3-coming-up">
      {sectionContent}
    </section>
  ) : (
    <Card
      component="section"
      variant="sunken"
      disablePadding
      className={styles.root}
      aria-labelledby="home3-coming-up-title"
      data-testid="home3-coming-up"
    >
      {sectionContent}
    </Card>
  );
}
